import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataProvider,
  QBox,
  QCheckbox,
  QDataTable,
  QFormControl,
  QHeading,
  QIcon,
  QInput,
  QText,
  QTooltip,
} from '@qualio/ui-components';
import {
  EventStepType,
  EventTemplateFormValues,
  EventTemplateStepCreateEdit,
  EventTemplateStepCreateEditType,
} from '../../../../types';
import { trimAndConvertToNumber } from '../../../../types/eventTemplateDetails';
import StepRowActions from '../../../../components/TemplateStepsList/components/StepRowActions';
import ContentTemplateDrawer from '../../../../components/TemplateStepsList/components/ContentTemplateDrawer';
import FormTemplateDrawer from '../../../../components/TemplateStepsList/components/FormTemplateDrawer';
import { useFlags } from '../../../../external/LaunchDarklyAdapter';

type EventTemplateFormStepsTableProps = {
  companyId: number;
  templateSteps: EventTemplateFormValues['steps'];
  setTemplateSteps: (steps: EventTemplateFormValues['steps']) => void;
  disableEdit: boolean;
  isTemplateReadonly: boolean;
  renameTemplateStep: (stepIndex: number) => void;
  changeValidationStepType: (stepIndex: number) => void;
};

const getStepTypeLabel = (t: (key: string, options?: { [key: string]: string }) => string, type: EventStepType) => {
  switch (type) {
    case 'document':
      return t('common.labels.content');
    case 'actions':
      return t('common.labels.tasks');
    case 'form':
      return t('common.labels.form');
    default:
      return '';
  }
};

export const getStepLabel = (
  t: (key: string, options?: { [key: string]: string }) => string,
  templateSteps: EventTemplateFormValues['steps'],
  step: EventTemplateStepCreateEditType,
): string => {
  const typeLabel = getStepTypeLabel(t, step.type);
  return step.order === templateSteps.length ? t('common.labels.validationWithType', { type: typeLabel }) : typeLabel;
};

export const EventTemplateFormStepsTable = ({
  companyId,
  templateSteps,
  setTemplateSteps,
  disableEdit,
  isTemplateReadonly,
  renameTemplateStep,
  changeValidationStepType,
}: EventTemplateFormStepsTableProps) => {
  const { t } = useTranslation();
  const [selectedStep, setSelectedStep] = useState<EventTemplateStepCreateEdit | null>(null);
  const isStepDueDates = useFlags('issueStepDueDates');

  const closeTaskDrawer = useCallback(() => {
    setSelectedStep(null);
  }, [setSelectedStep]);

  const deleteTemplateStep = useCallback(
    (step: EventTemplateStepCreateEdit) => {
      const stepIndex = step.order - 1;
      templateSteps.splice(stepIndex, 1);
      setTemplateSteps([...templateSteps]);
      closeTaskDrawer();
    },
    [closeTaskDrawer, setTemplateSteps, templateSteps],
  );

  const onStepClick = useCallback(
    (step: EventTemplateStepCreateEditType) => {
      if (step.template_id || step.form_template_id) {
        setSelectedStep(step);
      }
    },
    [setSelectedStep],
  );

  const preventStepDelete = useCallback(
    (step: EventTemplateStepCreateEditType) => {
      return step.order === templateSteps.length;
    },
    [templateSteps],
  );

  const columns = useMemo(
    () => [
      {
        header: t('common.labels.order'),
        accessorKey: 'order',
        enableSorting: false,
        meta: {
          minWidth: '150px',
          maxWidth: '150px',
        },
      },
      {
        header: t('common.labels.name'),
        accessorKey: 'name',
        enableSorting: false,
        cell: ({ row: { original: step } }: { row: { original: EventTemplateStepCreateEditType } }) => {
          return ((step.type === 'document' && step.template_id) || (step.type === 'form' && step.form_template_id)) &&
            !disableEdit ? (
            <QBox color={'blue.500'} cursor={'pointer'} onClick={() => onStepClick(step)}>
              {step.label}
            </QBox>
          ) : (
            <>{step.label}</>
          );
        },
      },
      {
        header: t('common.labels.type'),
        accessorKey: 'type',
        enableSorting: false,
        cell: ({ row: { original: step } }: { row: { original: EventTemplateStepCreateEditType } }) =>
          getStepLabel(t, templateSteps, step),
        meta: {
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
      {
        header: t('common.labels.qualityApproverRequired'),
        id: 'qualityApproverRequired',
        enableSorting: false,
        cell: ({ row: { original: step } }: { row: { original: EventTemplateStepCreateEditType } }) => {
          if (step.type === 'actions') {
            return null;
          }
          return (
            <QCheckbox
              isChecked={step.quality_approval_required}
              isDisabled={disableEdit || isTemplateReadonly}
              onChange={(e) => {
                setTemplateSteps(
                  templateSteps.map((s) => (s === step ? { ...s, quality_approval_required: e.target.checked } : s)),
                );
              }}
            />
          );
        },
        meta: {
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
      ...(isStepDueDates
        ? [
            {
              header: () => (
                <QBox display="inline-flex" alignItems="center" gap={1}>
                  {t('templates.steps.dueDays.header')}
                  <QTooltip label={t('templates.steps.dueDays.tooltip')}>
                    <QIcon iconName="Info" size="sm" data-cy="step-due-days-tooltip" />
                  </QTooltip>
                </QBox>
              ),
              accessorKey: 'time_limit',
              enableSorting: false,
              cell: ({ row: { original: step } }: { row: { original: EventTemplateStepCreateEditType } }) => {
                const isInvalid = !Number.isInteger(step.time_limit) || step.time_limit < 0 || step.time_limit > 100000;
                return (
                  <QFormControl
                    isInvalid={isInvalid}
                    error={isInvalid ? t('templates.steps.dueDays.validation.range') : undefined}
                  >
                    <QInput
                      type="number"
                      min={0}
                      value={String(step.time_limit ?? 0)}
                      // Task (actions) steps don't support a fixed due-days offset, so grey them out.
                      // Otherwise gate only on read-only: do NOT disable on disableEdit (in-flight
                      // autosave), because disabling a focused input blurs it and the user loses their
                      // place while typing. (QEV-4982 focus fix; debounce is handled separately in QEV-4983.)
                      isDisabled={isTemplateReadonly || step.type === 'actions'}
                      data-cy="step-due-days-input"
                      onChange={(e) => {
                        const next = trimAndConvertToNumber(e.target.value);
                        setTemplateSteps(
                          templateSteps.map((s) =>
                            s === step
                              ? { ...s, time_limit: next === '' || next === null || next === undefined ? 0 : next }
                              : s,
                          ),
                        );
                      }}
                    />
                  </QFormControl>
                );
              },
              meta: {
                minWidth: '150px',
                maxWidth: '150px',
              },
            },
          ]
        : []),
      {
        header: '',
        id: 'step',
        enableSorting: false,
        cell: ({ row: { original: step } }: { row: { original: EventTemplateStepCreateEditType } }) => {
          return (
            <StepRowActions
              step={step}
              templateSteps={templateSteps}
              onDelete={deleteTemplateStep}
              setTemplateSteps={setTemplateSteps}
              renameTemplateStep={renameTemplateStep}
              changeValidationStepType={changeValidationStepType}
              disableEdit={disableEdit}
              isTemplateReadonly={isTemplateReadonly}
            />
          );
        },
        meta: {
          minWidth: '200px',
          maxWidth: '200px',
        },
      },
    ],
    [
      templateSteps,
      deleteTemplateStep,
      setTemplateSteps,
      renameTemplateStep,
      changeValidationStepType,
      disableEdit,
      isTemplateReadonly,
      onStepClick,
      t,
      isStepDueDates,
    ],
  );

  return (
    <>
      <QHeading size="md">{t('common.labels.steps')}</QHeading>
      <DataProvider.Fixed data={templateSteps}>
        <QDataTable columns={columns} hideItemCount data-cy="event-template-steps-table" />
      </DataProvider.Fixed>
      {templateSteps.some((step) => !step.quality_approval_required) && (
        <QText fontSize="sm" color="blackAlpha.600" data-cy="optional-quality-approver-helper">
          {t('templates.messages.optionalQualityApproverHelper')}
        </QText>
      )}
      {selectedStep && selectedStep.type === 'document' && (
        <QBox data-cy="content-drawer-screen">
          <ContentTemplateDrawer
            key={selectedStep.template_id}
            onClose={closeTaskDrawer}
            onDelete={deleteTemplateStep}
            preventStepDelete={preventStepDelete}
            step={selectedStep}
            isTemplateReadonly={isTemplateReadonly}
          />
        </QBox>
      )}
      {selectedStep && selectedStep.type === 'form' && (
        <QBox data-cy="form-drawer-screen">
          <FormTemplateDrawer
            companyId={companyId}
            key={selectedStep.template_id}
            onClose={closeTaskDrawer}
            onDelete={deleteTemplateStep}
            preventStepDelete={preventStepDelete}
            step={selectedStep}
            isTemplateReadonly={isTemplateReadonly}
          />
        </QBox>
      )}
    </>
  );
};
